import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ColDef,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  PaginationChangedEvent,
  PaginationNumberFormatterParams,
  ValueParserParams,
} from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { IField } from '@shared/model';
import { ICapexMin } from '@shared/model/capital-investments';
import { DeletePopupComponent } from '../../popups/delete-popup/delete-popup.component';
import { RowActionComponent } from '../row-action/row-action.component';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicTableComponent implements OnInit, OnChanges {
  @Input() tableValue: any;
  @Input() caption: string = 'CAPEX_CREATED_OBJECTS';
  @Input() config?: any;
  @Input() dataValue: ColDef[] = [];
  @Input() selectedVariantId!: number | undefined | null;
  @Input() capexs?: ICapexMin[] | null;
  @Input() canCreate!: boolean;
  @Input() canCopy: boolean = true;
  @Input() selectedCapexId!: number | undefined | null;
  /** Права на строковые действия (UX-гейтинг row-action): по умолчанию всё разрешено. */
  @Input() permEdit = true;
  @Input() permCreate = true;
  @Input() permDelete = true;
  @Output() onCreateObject: EventEmitter<number> = new EventEmitter<number>();
  @Output() onCopyObject: EventEmitter<number> = new EventEmitter<number>();
  @Output() onDeleteObject: EventEmitter<number> = new EventEmitter<number>();
  @Output() onSetSelectObjectId: EventEmitter<number> = new EventEmitter<number>();
  @Output() onUpdateObject: EventEmitter<{ id: number; field: IField }> = new EventEmitter<{
    id: number;
    field: IField;
  }>();

  public noRowsTemplate: string = `<span>Нет обьектов</span>`;
  private rowId: string = '';
  private gridApi!: GridApi;
  private isShowAction$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => params.data.id;
  private colId!: string;
  public showDeleteBtn: boolean = false;
  public context: any = '';
  public defaultColDef: ColDef = {
    flex: 1,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public pivotPanelShow: 'always' | 'onlyWhenPivoting' | 'never' = 'always';
  public paginationPageSize: number = 10;
  public paginationCurrentPage!: number;
  public paginationtotalPages!: number;
  public columnDefs: ColDef[] = [];
  public dataLength: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  constructor(
    private _dialog: MatDialog,
    private scroller: ViewportScroller,
  ) {
    this.context = { componentParent: this };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableValue'] && this.tableValue?.length) {
      this.dataLength.next(this.tableValue.length);
    }
    if (changes['dataValue']) {
      this.applyColumnDefs();
    }
  }

  ngOnInit(): void {
    this.applyColumnDefs();
  }

  private applyColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: '',
        field: 'checkbox',
        maxWidth: 35,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        showDisabledCheckboxes: true,
      },
      ...(this.dataValue.map((col) => {
        col.editable = this.permEdit;
        return col;
      }) ?? []),
      {
        headerName: '',
        field: 'action',
        minWidth: 100,
        cellRenderer: RowActionComponent,
        cellRendererParams: {
          selectedId: this.isShowAction$,
          dataLenght: this.dataLength,
          canCreate: this.canCreate,
          canCopy: this.canCopy,
        },
      },
    ];

    if (!this.permDelete) {
      this.columnDefs.shift();
    }

    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.columnDefs);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  getLastSelectedNode() {
    let selectedRows = this.gridApi.getSelectedRows();
    let allRowsCount = this.gridApi.getDisplayedRowCount();
    if (selectedRows.length >= 1 && selectedRows.length < allRowsCount) {
      this.showDeleteBtn = true;
    } else {
      this.showDeleteBtn = false;
    }
  }

  openDeletePopup(id: number) {
    const dialogRef = this._dialog.open(DeletePopupComponent, {
      data: {
        title: 'DELETE_MODAL_PAGE_OBLECT_DELETE',
        description: 'DELETE_MODAL_PAGE_OBLECT_INFO',
      },
    });
    dialogRef.afterClosed().subscribe((isDelete: boolean) => {
      if (isDelete) {
        this.onDeleteObject.emit(id);
      }
    });
  }

  deleteGroupRows() {
    const dialogRef = this._dialog.open(DeletePopupComponent, {
      data: {
        title: 'DELETE_MODAL_PAGE_OBLECTS_DELETE',
        description: 'DELETE_MODAL_PAGE_OBLECTS_INFO',
      },
    });
    dialogRef.afterClosed().subscribe((isDelete: boolean) => {
      if (isDelete) {
        let selectedRows = this.gridApi.getSelectedRows();
        selectedRows.forEach((item) => this.onDeleteObject.emit(item.id));
      }
    });
  }

  onFilterTextBoxChanged() {
    this.gridApi.setQuickFilter((document.getElementById('filter-text-box') as HTMLInputElement).value);
  }

  onSelectionChanged(event: any) {}

  onRowSelected(event: any) {
    if (event.node.selected) {
      this.onSetSelectObjectId.emit(event.data.id);
      this.rowId = event.data.id;
      this.isShowAction$.next(event.data.id);
    } else if (!event.node.selected && this.rowId == event.data.id) {
      this.isShowAction$.next(null);
      this.gridApi.stopEditing();
    }

    this.getLastSelectedNode();
  }

  onCellValueChanged(event: any) {
    this.updateObject(event.colDef.field, event.newValue);
  }

  onCellFocused(event: any) {
    this.colId = event.column.colId;
  }

  onCellClicked(event: any) {}

  cellActionsShow(params: any): Object | undefined {
    return params.node.selected
      ? {
          component: RowActionComponent,
        }
      : undefined;
  }

  cellMethodCopy(objectId: number): void {
    if (this.tableValue?.length !== 10) {
      this.onCopyObject.emit(objectId as number);
    }
  }

  cellMethodDelete(objectId: number): void {
    // if (this.tableValue?.length !== 1) {
    this.openDeletePopup(objectId as number);
    // }
  }

  cellMethodSelect(value: string) {
    this.updateObject(this.colId, +value);
  }

  isEditable(params: any) {
    return params.node.selected;
  }

  numberParser(params: ValueParserParams): number | string {
    return Number.isNaN(Number(params.newValue)) ? params.oldValue : Number(params.newValue);
  }

  updateObject(name: string, value: any): void {
    this.onUpdateObject.emit({ id: +this.rowId as number, field: { name, value } });
  }

  goTo() {
    // this.scroller.scrollToAnchor("choice-of-calculation-options");
    this.scroller.scrollToAnchor('scrollTop');
  }

  onPaginationChanged(event: PaginationChangedEvent<any>) {
    if (this.gridApi) {
      this.paginationCurrentPage = this.gridApi.paginationGetCurrentPage() + 1;
      this.paginationtotalPages = this.gridApi.paginationGetTotalPages();
    }
  }

  paginationNumberFormatter: (params: PaginationNumberFormatterParams) => string = (
    params: PaginationNumberFormatterParams,
  ) => {
    return params.value.toLocaleString();
  };

  onBtNext() {
    this.gridApi.paginationGoToNextPage();
  }

  onBtPrevious() {
    this.gridApi.paginationGoToPreviousPage();
  }
}
